import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import App from '../../App';
import VerifyTaskView from '../verify/VerifyTaskView';
import BlockingLoadingOverlay from '../common/BlockingLoadingOverlay';
import { routingJobsApi } from '../../api/routingJobsApi';
import { getTenantModelDefinition, TenantModelDefinition } from '../../modules/tenantRegistry';
import { VerifyPayload } from '../../api/wmService';

// Create a wrapper around App component to handle the props properly
interface AppWithViewProps {
  view: 'taskSummary' | 'taskDetail' | 'userInfo' | 'userManagement' | 'controlTower';
  hubId?: string;
  taskId?: string;
}

const AppWithView: React.FC<AppWithViewProps> = ({ view, hubId, taskId }) => {
  // @ts-ignore: Let's ignore type errors for now since we know the prop is correct
  return <App initialView={view} />;
};

// Wrapper for the task summary view
export const TaskSummaryWrapper: React.FC = () => {
  const { hubId } = useParams<{ hubId: string }>();
  
  // Log URL parameters to verify they're being captured correctly
  console.log(`TaskSummaryWrapper: hubId=${hubId}`);
  
  return <AppWithView view="taskSummary" hubId={hubId} />;
};

// Wrapper for the task detail view
export const TaskDetailWrapper: React.FC = () => {
  const { hubId, taskId } = useParams<{ hubId: string; taskId: string }>();
  const navigate = useNavigate();
  
  // Log URL parameters to verify they're being captured correctly
  console.log(`TaskDetailWrapper: hubId=${hubId}, taskId=${taskId}`);
  
  useEffect(() => {
    // Validate that we have both required parameters
    if (!hubId || !taskId) {
      console.error("Missing hubId or taskId parameter");
      navigate("/task-summary");
    }
  }, [hubId, taskId, navigate]);
  
  return <AppWithView view="taskDetail" hubId={hubId} taskId={taskId} />;
};

// Wrapper for the user info view
export const UserInfoWrapper: React.FC = () => {
  const { hubId } = useParams<{ hubId: string }>();
  
  // Log URL parameters to verify they're being captured correctly
  console.log(`UserInfoWrapper: hubId=${hubId}`);
  
  return <AppWithView view="userInfo" hubId={hubId} />;
};

// Wrapper for the user management view
export const UserManagementWrapper: React.FC = () => {
  const { hubId } = useParams<{ hubId: string }>();

  return <AppWithView view="userManagement" hubId={hubId} />;
};

export const ControlTowerWrapper: React.FC = () => <AppWithView view="controlTower" />;

// Wrapper for the verify task view
export const VerifyTaskWrapper: React.FC = () => {
  const { hubId, taskId } = useParams<{ hubId: string; taskId: string }>();
  const navigate = useNavigate();
  const [tenantDefinition, setTenantDefinition] = useState<TenantModelDefinition | null>(null);
  const [initialPayload, setInitialPayload] = useState<VerifyPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Log URL parameters to verify they're being captured correctly
  console.log(`VerifyTaskWrapper: hubId=${hubId}, taskId=${taskId}`);

  useEffect(() => {
    // Validate that we have both required parameters
    if (!hubId || !taskId) {
      console.error("Missing hubId or taskId parameter");
      navigate("/task-summary");
      return;
    }
    setLoading(true);
    setError(null);
    setInitialPayload(null);
    routingJobsApi.getVerifyWorkspace(hubId, taskId)
      .then((workspace) => {
        const def = getTenantModelDefinition(workspace.job.depotSnapshot?.tenantId, workspace.job.depotSnapshot?.modelType);
        const adaptVerifyPayload = def.adaptVerifyPayloadFromViews;
        if (!adaptVerifyPayload) {
          throw new Error('Verify workspace is not supported for this tenant');
        }
        const viewsById = Object.fromEntries((workspace.views ?? []).map((view) => [view.viewId, view]));
        setTenantDefinition(def);
        setInitialPayload(adaptVerifyPayload(workspace.job, viewsById));
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err?.message || 'Failed to load job metadata');
        setLoading(false);
      });
  }, [hubId, taskId, navigate]);

  if (!hubId || !taskId) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <BlockingLoadingOverlay
          open
          label="Loading verification"
          detail="Resolving job metadata and selecting the correct workspace."
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const fetchPayload = tenantDefinition?.fetchVerifyPayload ?? undefined;
  return (
      <VerifyTaskView
        hubId={hubId}
        taskId={taskId}
        initialPayload={initialPayload}
        fetchVerifyPayload={fetchPayload}
        tenantDefinition={tenantDefinition}
      />
  );
}; 
